import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudienceStatus,
  AudienceTier,
  downloadAudience,
  getAudienceStatus,
  startAudiencePull,
} from '../../lib/api';

type Group = 'ad' | 'ever';

const GROUP_FILES: Record<Group, { tier: AudienceTier; label: string }[]> = {
  ad: [
    { tier: 'ad1', label: 'AD1' },
    { tier: 'ad2', label: 'AD2' },
    { tier: 'ad3', label: 'AD3' },
  ],
  ever: [{ tier: 'ever', label: 'AD1-ever' }],
};

const POLL_MS = 10000;

// Pooled SMS donor audiences. The pull runs server-side as a BigQuery job, so
// closing the tab doesn't stop it: come back and click again to download.
export const SmsAudiences: React.FC = () => {
  const [status, setStatus] = useState<AudienceStatus | null>(null);
  const [pending, setPending] = useState<Group | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string[]>([]);
  // Mirror of `pending` for the polling callback, which outlives renders.
  const pendingRef = useRef<Group | null>(null);

  const download = useCallback(async (group: Group, prefix: string) => {
    setDownloading(true);
    try {
      const names: string[] = [];
      for (const { tier, label } of GROUP_FILES[group]) {
        const name = `${prefix}-${label}.csv`;
        await downloadAudience(tier, name);
        names.push(name);
      }
      setDone(names);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
      pendingRef.current = null;
      setPending(null);
    }
  }, []);

  const handleStatus = useCallback((s: AudienceStatus) => {
    setStatus(s);
    const group = pendingRef.current;
    if (s.state === 'ready' && group) {
      download(group, s.file_prefix);
    } else if (s.state === 'failed') {
      setError(s.error || 'The audience pull failed. Click the button to try again.');
      pendingRef.current = null;
      setPending(null);
    }
  }, [download]);

  useEffect(() => {
    getAudienceStatus().then(setStatus).catch(() => { /* buttons still work */ });
  }, []);

  useEffect(() => {
    if (status?.state !== 'running') return;
    const id = window.setInterval(() => {
      getAudienceStatus().then(handleStatus).catch(() => { /* retry next tick */ });
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [status?.state, handleStatus]);

  const handleClick = async (group: Group) => {
    setError(null);
    setDone([]);
    pendingRef.current = group;
    setPending(group);
    try {
      handleStatus(await startAudiencePull());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the pull');
      pendingRef.current = null;
      setPending(null);
    }
  };

  const running = status?.state === 'running';
  const busy = downloading || (pending !== null && !error);

  return (
    <div className="sms-audiences">
      <h3>SMS Donor Audiences</h3>
      <p className="donor-export-desc">
        Donors across all accounts, SMS donations only. AD1, AD2 and AD3 are donors who gave
        exactly 1, exactly 2, or 3+ times in the last 365 days, and no phone is in more than one
        of them. AD1-ever is donors who gave exactly once, ever.
      </p>

      <div className="donor-export-controls">
        <button className="btn btn-primary" disabled={busy} onClick={() => handleClick('ad')}>
          {pending === 'ad' && !error ? 'Working…' : 'Download ad1, ad2, ad3 audiences'}
        </button>
        <button className="btn btn-primary" disabled={busy} onClick={() => handleClick('ever')}>
          {pending === 'ever' && !error ? 'Working…' : 'Download ad1-ever audience'}
        </button>
      </div>

      {running && (
        <p className="donor-export-note">
          Pulling audiences — this takes about 5 minutes. You can close this tab; the pull keeps
          running. Come back and click the button to download.
        </p>
      )}
      {downloading && <p className="donor-export-note">Downloading…</p>}
      {status?.state === 'ready' && !pending && done.length === 0 && (
        <p className="donor-export-note">Today's audiences are ready. Click a button to download.</p>
      )}
      {done.length > 0 && (
        <p className="donor-export-success">Downloaded: {done.join(', ')}</p>
      )}
      {error && <p className="donor-export-error">Error: {error}</p>}
    </div>
  );
};
